// Cliente del canal de eventos en vivo de la Sala y del calendario para el HCE (Sala CRM<->HCE, rebanada 3; PLAN.md 4.4 y 9-C6).
//
// El servidor solo AVISA "cambió la Sala" / "cambió el calendario" (`{tipo, n}`, sin datos); quien lo usa vuelve a pedir lo suyo. Mismo
// diseño que el cliente del CRM (packages/web/src/web/lib/eventos-sala.ts) más una pieza propia del HCE: VARIAS PESTAÑAS.
//
//   - Una sola pestaña (la «líder», elegida con Web Locks) mantiene la conexión y pide el ticket; las demás reciben los avisos y el estado
//     por BroadcastChannel. Así no se multiplican conexiones (límite por usuario en el servidor) ni se repite cada acción. Si la líder se
//     cierra, el bloqueo pasa solo a otra pestaña, que abre su propia conexión.
//   - `esLider` queda disponible para lo que deba actuar UNA sola vez entre pestañas (avisos con sonido, botón «Conectar»: rebanadas 5 y 8).
//   - Sin Web Locks (navegador viejo) cada pestaña se conecta por su cuenta: es la degradación segura, sin duplicar avisos.
//
// Autenticación: `EventSource` no admite cabeceras y el id de sesión NO va en la URL. Se pide un ticket de un solo uso (60 s) con la sesión en
// la cabecera y se abre el flujo con ese ticket. Como el ticket se gasta al abrir, el reintento automático de `EventSource` reutilizaría uno
// muerto: aquí se cierra el `EventSource` en cuanto falla y se reconecta a mano con un ticket nuevo y backoff.
//
// Este archivo no toca React ni el DOM al importarse (todo es inyectable), para poder probarlo sin navegador ni reloj real.

export type TipoEventoSala = 'sala' | 'calendario';
/**
 * «conectando» = primer intento; «en-vivo» = flujo abierto; «reconectando» = se cayó y se reintenta (la pantalla lo muestra);
 * «no-disponible» = el servidor dijo 403 (este rol no tiene canal): no se reintenta ni se muestra nada, el respaldo periódico basta.
 */
export type EstadoEnVivo = 'conectando' | 'en-vivo' | 'reconectando' | 'no-disponible';

/** Lo mínimo de `EventSource` que se usa (permite inyectar uno falso en las pruebas). */
export type FuenteEventos = {
  addEventListener: (tipo: string, fn: (ev: { data?: string }) => void) => void;
  close: () => void;
};

export type ResultadoTicket = { ok: true; ticket: string } | { ok: false; status: number };

export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_MAX_MS = 30_000;
/** Pausa mínima antes de reabrir tras el `fin` del servidor (un servidor que cerrara en bucle no puede provocar un bucle cerrado aquí). */
export const REABRIR_TRAS_FIN_MS = 250;

/** Espera antes del intento número `intento` (1, 2, 3...): exponencial con tope y jitter de hasta +25 %. */
export function esperaBackoff(intento: number, aleatorio: () => number = Math.random): number {
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, intento - 1));
  return Math.min(BACKOFF_MAX_MS, Math.round(base * (1 + 0.25 * aleatorio())));
}

export function esTipoEvento(v: unknown): v is TipoEventoSala {
  return v === 'sala' || v === 'calendario';
}

export function esEstadoEnVivo(v: unknown): v is EstadoEnVivo {
  return v === 'conectando' || v === 'en-vivo' || v === 'reconectando' || v === 'no-disponible';
}

export type OpcionesConexionEnVivo = {
  /** Pide el ticket al servidor (con la sesión en cabecera). Nunca debe lanzar: un fallo de red es `{ok:false, status:0}`. */
  pedirTicket: () => Promise<ResultadoTicket>;
  urlFlujo: (ticket: string) => string;
  crearFuente: (url: string) => FuenteEventos;
  alCambio: (tipo: TipoEventoSala) => void;
  alEstado: (estado: EstadoEnVivo) => void;
  programar?: (fn: () => void, ms: number) => unknown;
  cancelar?: (handle: unknown) => void;
  /** 0..1, para el jitter del backoff. */
  aleatorio?: () => number;
};

/** Una conexión SSE con reconexión (la usa la pestaña líder). */
export function crearConexionEnVivo(opciones: OpcionesConexionEnVivo) {
  const programar = opciones.programar ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const cancelar = opciones.cancelar ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const aleatorio = opciones.aleatorio ?? Math.random;

  let activa = false;
  /** Cada arranque/parada incrementa la generación: lo que llegue tarde de una conexión vieja se ignora. */
  let generacion = 0;
  let fuente: FuenteEventos | null = null;
  let temporizador: unknown = null;
  let intentos = 0;
  let estado: EstadoEnVivo = 'conectando';

  function poner(nuevo: EstadoEnVivo): void {
    if (estado === nuevo) return;
    estado = nuevo;
    opciones.alEstado(nuevo);
  }

  function cerrarFuente(): void {
    if (!fuente) return;
    try {
      fuente.close();
    } catch {
      // ya cerrada
    }
    fuente = null;
  }

  function reintentar(gen: number, espera: number): void {
    if (gen !== generacion || !activa) return;
    temporizador = programar(() => {
      temporizador = null;
      void conectar(gen);
    }, espera);
  }

  async function conectar(gen: number): Promise<void> {
    if (gen !== generacion || !activa) return;
    let t: ResultadoTicket;
    try {
      t = await opciones.pedirTicket();
    } catch {
      t = { ok: false, status: 0 };
    }
    if (gen !== generacion || !activa) return;
    if (!t.ok && t.status === 403) {
      // Un rol sin permiso no lo tendrá al reintentar: se queda sin canal, sin indicador y sin martillar al servidor.
      poner('no-disponible');
      return;
    }
    if (!t.ok) {
      intentos++;
      poner('reconectando');
      // Sesión vencida (401): no tiene sentido insistir rápido; va al ritmo más lento y el respaldo periódico sigue.
      reintentar(gen, t.status === 401 ? BACKOFF_MAX_MS : esperaBackoff(intentos, aleatorio));
      return;
    }

    let f: FuenteEventos;
    try {
      f = opciones.crearFuente(opciones.urlFlujo(t.ticket));
    } catch {
      intentos++;
      poner('reconectando');
      reintentar(gen, esperaBackoff(intentos, aleatorio));
      return;
    }
    fuente = f;

    f.addEventListener('open', () => {
      if (gen !== generacion) return;
      intentos = 0;
      poner('en-vivo');
    });
    f.addEventListener('cambio', (ev) => {
      if (gen !== generacion) return;
      try {
        const tipo = (JSON.parse(ev.data ?? '{}') as { tipo?: unknown }).tipo;
        if (esTipoEvento(tipo)) opciones.alCambio(tipo);
      } catch {
        // un aviso ilegible no rompe el canal: el respaldo periódico lo cubre
      }
    });
    // El servidor cierra cada ~5 min para exigir sesión vigente: se reabre con un ticket nuevo, sin castigo de backoff.
    f.addEventListener('fin', () => {
      if (gen !== generacion) return;
      cerrarFuente();
      reintentar(gen, REABRIR_TRAS_FIN_MS);
    });
    f.addEventListener('error', () => {
      if (gen !== generacion) return;
      cerrarFuente();
      intentos++;
      poner('reconectando');
      reintentar(gen, esperaBackoff(intentos, aleatorio));
    });
  }

  function iniciar(): void {
    if (activa) return;
    activa = true;
    generacion++;
    intentos = 0;
    estado = 'conectando';
    opciones.alEstado('conectando');
    void conectar(generacion);
  }

  function detener(): void {
    activa = false;
    generacion++;
    if (temporizador !== null) {
      cancelar(temporizador);
      temporizador = null;
    }
    cerrarFuente();
  }

  return { iniciar, detener, estado: () => estado };
}

// --- Varias pestañas ------------------------------------------------------------------------------------------------------------------

/** Lo mínimo de `navigator.locks` que se usa. */
export type AdministradorBloqueos = {
  request: (nombre: string, opciones: { signal?: AbortSignal }, cb: () => Promise<unknown>) => Promise<unknown>;
  /** Qué bloqueos hay ahora mismo (Web Locks `query`): el aviso con sonido lo usa para saber si alguna pestaña visible tiene el suyo. */
  query?: () => Promise<{ held?: { name?: string }[] }>;
};

/** Lo mínimo de `BroadcastChannel` que se usa. */
export type CanalEntrePestanas = {
  postMessage: (mensaje: unknown) => void;
  close: () => void;
  onmessage: ((ev: { data: unknown }) => void) | null;
};

export type OpcionesEntrePestanas = {
  /** Nombre del bloqueo y del canal (por origen: cada consultorio vive en su subdominio). */
  clave: string;
  /** Crea la conexión SSE de la pestaña líder. */
  crearConexion: (cb: { alCambio: (t: TipoEventoSala) => void; alEstado: (e: EstadoEnVivo) => void }) => { iniciar: () => void; detener: () => void };
  alCambio: (tipo: TipoEventoSala) => void;
  alEstado: (estado: EstadoEnVivo) => void;
  /** Avisa cuando esta pestaña pasa a ser (o deja de ser) la que actúa. Sin Web Locks toda pestaña es líder. */
  alLider?: (esLider: boolean) => void;
  /**
   * Esta pestaña se vuelve líder TRAS otra que ya conducía el canal (la anterior se cerró): durante el hueco del traspaso no llegaron
   * avisos, así que quien usa el canal debe recargar. No se llama en la primera pestaña (nadie conducía antes).
   */
  alTraspaso?: () => void;
  bloqueos?: AdministradorBloqueos | null;
  crearCanal?: ((nombre: string) => CanalEntrePestanas) | null;
};

type MensajePestana = { k: 'cambio'; tipo: TipoEventoSala } | { k: 'estado'; estado: EstadoEnVivo } | { k: 'hola' };

export function crearEventosEntrePestanas(o: OpcionesEntrePestanas) {
  let activa = false;
  let canal: CanalEntrePestanas | null = null;
  let conexion: { iniciar: () => void; detener: () => void } | null = null;
  let liberar: (() => void) | null = null;
  let abortar: AbortController | null = null;
  let estadoLider: EstadoEnVivo = 'conectando';
  /** Se oyó a otra pestaña conducir el canal: si esta llega a ser líder después, es un traspaso. */
  let huboLider = false;

  function volverseLider(): void {
    if (!activa) return;
    o.alLider?.(true);
    if (huboLider) o.alTraspaso?.();
    conexion = o.crearConexion({
      alCambio: (tipo) => {
        o.alCambio(tipo);
        canal?.postMessage({ k: 'cambio', tipo } satisfies MensajePestana);
      },
      alEstado: (estado) => {
        estadoLider = estado;
        o.alEstado(estado);
        canal?.postMessage({ k: 'estado', estado } satisfies MensajePestana);
      },
    });
    conexion.iniciar();
  }

  function iniciar(): void {
    if (activa) return;
    activa = true;
    const bloqueos = o.bloqueos ?? null;
    if (!bloqueos) {
      // Sin Web Locks no hay forma segura de elegir una líder: cada pestaña se conecta sola y no escucha a las demás (no duplica avisos).
      volverseLider();
      return;
    }
    if (o.crearCanal) {
      canal = o.crearCanal(o.clave);
      canal.onmessage = (ev) => {
        const m = ev.data as MensajePestana | null;
        if (!m || typeof m !== 'object') return;
        if (m.k === 'cambio' && esTipoEvento(m.tipo)) {
          huboLider = true;
          o.alCambio(m.tipo);
        } else if (m.k === 'estado' && esEstadoEnVivo(m.estado)) {
          huboLider = true;
          o.alEstado(m.estado);
        }
        // Una pestaña recién abierta pregunta cómo va el canal: solo responde la líder (la única con conexión).
        else if (m.k === 'hola' && conexion) canal?.postMessage({ k: 'estado', estado: estadoLider } satisfies MensajePestana);
      };
      canal.postMessage({ k: 'hola' } satisfies MensajePestana);
    }
    o.alLider?.(false);
    abortar = new AbortController();
    // La petición queda esperando hasta que esta pestaña sea la líder; se libera al cerrar la pestaña (el navegador lo hace solo) o al detener.
    bloqueos
      .request(o.clave, { signal: abortar.signal }, () => new Promise<void>((resolver) => {
        // Si se detuvo entre la concesión del bloqueo y este callback, no hay a quién conducir: se suelta ya (si no, el bloqueo quedaría
        // retenido para siempre y ninguna otra pestaña llegaría a ser líder).
        if (!activa) {
          resolver();
          return;
        }
        liberar = resolver;
        volverseLider();
      }))
      .catch(() => {
        // AbortError al detener antes de ser líder: nada que limpiar
      });
  }

  function detener(): void {
    if (!activa) return;
    activa = false;
    abortar?.abort();
    abortar = null;
    conexion?.detener();
    conexion = null;
    liberar?.();
    liberar = null;
    if (canal) {
      canal.onmessage = null;
      canal.close();
      canal = null;
    }
    o.alLider?.(false);
  }

  return { iniciar, detener };
}

// --- Pegamento con el navegador --------------------------------------------------------------------------------------------------------

/** Pide el ticket del HCE: la sesión (id opaco) viaja en la cabecera, nunca en la URL. `cabeceras` y `url` los pone quien llama. */
export async function pedirTicketHce(url: string, cabeceras: Record<string, string>): Promise<ResultadoTicket> {
  try {
    const res = await fetch(url, { method: 'POST', headers: cabeceras, body: '{}' });
    if (!res.ok) return { ok: false, status: res.status };
    const cuerpo = (await res.json()) as { ticket?: unknown };
    return typeof cuerpo.ticket === 'string' ? { ok: true, ticket: cuerpo.ticket } : { ok: false, status: 0 };
  } catch {
    return { ok: false, status: 0 };
  }
}
